<template>
  <div
    class="alice-shell theme-turquoise"
    :class="{
      'alice-shell--compact': isChatRoute && !sidebarCollapsed,
      'alice-shell--collapsed': sidebarCollapsed,
    }"
  >
    <TopBar class="alice-shell__topbar">
      <template #breadcrumb>
        <a
          href="#"
          class="alice-tb-crumb"
          @click.prevent="$router.push('/projects')"
          >Projects</a
        >
        <span class="alice-tb-sep">›</span>
        <span class="alice-tb-crumb alice-tb-crumb--client">
          <span
            v-if="normalized && normalized.clientColor"
            class="alice-tb-crumb__mark"
            :style="{ background: normalized.clientColor }"
          />
          {{ normalized ? normalized.client : "" }}
        </span>
        <span class="alice-tb-sep">›</span>
        <span class="alice-tb-crumb alice-tb-crumb--current">{{
          project ? project.title : "Loading…"
        }}</span>
      </template>
    </TopBar>

    <Sidebar
      class="alice-shell__sidebar"
      variant="rail"
      active-key="projects"
    />

    <ProjectSubSidebar
      class="alice-shell__subsidebar"
      :project-id="$route.params.id"
      :client-name="normalized ? normalized.client : ''"
      :client-color="normalized ? normalized.clientColor : ''"
      :title="project ? project.title : 'Loading…'"
      :status="normalized ? normalized.status : ''"
      :saving="loading"
      :save-disabled="loading || !project"
      :tabs="tabs"
      :created-label="createdLabel"
      :last-edited-label="lastEditedLabel"
      :compact="isChatRoute"
      :collapsed="sidebarCollapsed"
      :readonly="isGuest"
      :show-menu="!isGuest"
      @save="saveAndSync"
      @menu="menuOpen = !menuOpen"
      @update-status="setStatus"
      @toggle-collapsed="toggleSidebar"
    />

    <main class="alice-shell__main">
      <router-view />
    </main>

    <v-menu v-model="menuOpen" :target="[0, 0]" location="end">
      <v-list class="alice-menu-list">
        <v-list-item v-if="project && project.report_link" @click="openReport">
          <v-list-item-title>View report</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="!isGuest" @click="createSlides">
          <v-list-item-title>Generate report</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      location="bottom right"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { functions, db } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc, collection, getDocs } from "firebase/firestore";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import ProjectSubSidebar from "@/components/alice/ProjectSubSidebar.vue";
import {
  normalizeProject,
  fmtDate,
  relTime,
} from "@/components/alice/normalize";

export default {
  name: "ProjectPage",
  components: { TopBar, Sidebar, ProjectSubSidebar },
  data() {
    return {
      snackbar: { show: false, text: "", color: "success" },
      menuOpen: false,
      sidebarCollapsed: this.readSidebarCollapsed(),
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
      loading: (state) => state.loading,
    }),
    isGuest() {
      return this.$store.getters.isGuest;
    },
    normalized() {
      return this.project ? normalizeProject(this.project) : null;
    },
    tabs() {
      const insightCount = this.flattenedInsightCount;
      const fileCount = this.project?.files?.length ?? null;
      const recCount = this.project?.recordings?.length ?? null;
      const all = [
        { key: "details", label: "Details" },
        { key: "earlier-research", label: "Eerder onderzoek" },
        { key: "obslog", label: "Obslog" },
        { key: "insights", label: "Insights", count: insightCount },
        { key: "chat", label: "Chat" },
        {
          key: "saved",
          label: "Notes",
          count: this.project?.saved?.length ?? null,
        },
        { key: "files", label: "Files", count: fileCount },
        { key: "recordings", label: "Recordings", count: recCount },
      ];
      // Guests don't get the Details tab (project metadata/client config) nor
      // Eerder onderzoek (other clients' research; the callable refuses them).
      const teamOnly = ["details", "earlier-research"];
      return this.isGuest ? all.filter((t) => !teamOnly.includes(t.key)) : all;
    },
    flattenedInsightCount() {
      const chapters = this.project?.chapters;
      if (!Array.isArray(chapters)) return 0;
      return chapters.reduce((sum, c) => sum + (c?.insights?.length || 0), 0);
    },
    createdLabel() {
      if (!this.normalized || !this.normalized.created) return "";
      return fmtDate(this.normalized.created);
    },
    lastEditedLabel() {
      // Firestore project docs don't carry an updatedAt today; show the
      // newest file timestamp as a proxy. Empty when no signal exists.
      const files = this.project?.files || [];
      if (!files.length) return "";
      const newest = files
        .map((f) => f.createdAt)
        .filter((v) => typeof v === "number")
        .sort((a, b) => b - a)[0];
      if (!newest) return "";
      return relTime(new Date(newest * 1000));
    },
    isChatRoute() {
      return /\/chat\/?$/.test(this.$route.path);
    },
  },
  mounted() {
    this.$store.dispatch("fetchProject", this.$route.params.id);
    this.$store.commit("setChat", []);
    // Guests can't read the clients collection (rules) — skip the fetch.
    if (!this.isGuest && this.$store.state.clients.length === 0) {
      this.$store.dispatch("fetchClients");
    }
  },
  methods: {
    readSidebarCollapsed() {
      try {
        return localStorage.getItem("valsplat.sidebarCollapsed") === "1";
      } catch {
        return false;
      }
    },
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      try {
        localStorage.setItem(
          "valsplat.sidebarCollapsed",
          this.sidebarCollapsed ? "1" : "0"
        );
      } catch {
        // localStorage unavailable (private mode, etc.) — non-fatal
      }
    },
    async saveAndSync() {
      await this.$store.dispatch("updateProject");
      await this.updateKnowledgeBase();
    },
    async createSlides() {
      this.$store.commit("setLoading", true);
      const createSlides = httpsCallable(functions, "createSlides");
      const res = await createSlides({ project_uid: this.project.uid });
      window.open(res.data, "_blank");
      this.project.report_link = res.data;
      this.$store.commit("setLoading", false);
    },
    openReport() {
      window.open(this.project.report_link, "_blank");
    },
    async setStatus(status) {
      if (!this.project) return;
      const valid = ["draft", "in_progress", "done"];
      if (!valid.includes(status)) return;
      const previous = this.project.status;
      // Optimistic update so the sub-sidebar tag flips immediately.
      this.project.status = status;
      try {
        await updateDoc(doc(db, "projects", this.project.uid), { status });
        // Refresh the global projects list so /projects rows/cards reflect
        // the new status without a manual reload.
        this.$store.dispatch("fetchProjects");
      } catch (err) {
        // Roll back on failure.
        this.project.status = previous;
        console.error("Failed to update project status:", err);
        this.showSnackbar(
          "Could not update status. Please try again.",
          "error"
        );
      }
    },
    showSnackbar(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    async updateKnowledgeBase() {
      this.$store.commit("setLoading", true);
      let hasError = false;

      try {
        const upsertProject = httpsCallable(
          functions,
          "upsertProjectToVectorDatabase"
        );
        const res = await upsertProject({ project: this.project });
        const qdrantId = res.data.qdrant_id;

        await updateDoc(doc(db, "projects", this.project.uid), {
          qdrant_id: qdrantId,
        });
        this.project.qdrant_id = qdrantId;
      } catch (err) {
        hasError = true;
        console.error("Upsert project failed:", err);
        this.showSnackbar(
          "Failed to update project in vector database. Please try again.",
          "error"
        );
      }

      try {
        const upsertMoments = httpsCallable(
          functions,
          "upsertMomentsToVectorDatabase"
        );
        const recordingsSnap = await getDocs(
          collection(db, "projects", this.project.uid, "recordings")
        );
        await Promise.all(
          recordingsSnap.docs.map(async (rec) => {
            const momentsSnap = await getDocs(
              collection(
                db,
                "projects",
                this.project.uid,
                "recordings",
                rec.id,
                "moments"
              )
            );
            const moments = momentsSnap.docs.map((d) => ({
              ...d.data(),
              id: d.id,
            }));
            if (moments.length > 0) {
              await upsertMoments({
                moments,
                projectId: this.project.uid,
                recordingId: rec.id,
                client_id: this.project.client_id,
              });
            }
          })
        );
      } catch (err) {
        hasError = true;
        console.error("Upsert moments failed:", err);
        this.showSnackbar(
          "Failed to update moments in vector database. Please try again.",
          "error"
        );
      }

      try {
        // Extract every typed file (proposal / test script / report) that has
        // not yet been extracted under its current type. Each type lands in its
        // own vector collection. Runs sequentially — extraction is heavy.
        const effectiveType = (f) =>
          f.fileType || (f.isFinalReport ? "report" : null);
        const needsExtraction = (f) => {
          const type = effectiveType(f);
          if (!type) return false;
          if (f.extractedAt) return f.extractedType !== type;
          return !f.insightsProcessed; // legacy reports
        };
        const toExtract = (this.project.files || []).filter(needsExtraction);

        if (toExtract.length) {
          const extractFile = httpsCallable(
            functions,
            "extractFileToVectorDatabase",
            { timeout: 540000 }
          );
          for (const file of toExtract) {
            const type = effectiveType(file);
            await extractFile({
              projectId: this.project.uid,
              fileUrl: file.url,
              fileName: file.name,
              fileId: file.id,
              fileType: type,
            });
            // Keep local state idempotent within this session.
            file.insightsProcessed = true;
            file.extractedType = type;
            file.extractedAt = Math.round(Date.now() / 1000);
          }
        }
      } catch (err) {
        hasError = true;
        console.error("Extract files failed:", err);
        this.showSnackbar(
          "Failed to extract one or more files into the vector database. Please try again.",
          "error"
        );
      }

      if (!hasError) {
        this.showSnackbar("Project updated successfully.");
      }
      this.$store.commit("setLoading", false);
    },
  },
};
</script>

<style scoped lang="scss">
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 280px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
  transition: grid-template-columns 0.18s ease;
}
.alice-shell--compact {
  grid-template-columns: 56px 220px 1fr;
}
.alice-shell--collapsed {
  grid-template-columns: 56px 28px 1fr;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
.alice-shell__subsidebar {
  grid-column: 2;
  grid-row: 2;
}
.alice-shell__main {
  grid-column: 3;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  background: var(--vp-white);
}

/* TopBar breadcrumb chips */
.alice-tb-crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: var(--vp-grijs-80);
  white-space: nowrap;
}
.alice-tb-crumb:hover {
  color: var(--vp-white);
}
.alice-tb-crumb--current {
  color: var(--vp-white);
  text-transform: none;
  letter-spacing: 0.04em;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.alice-tb-crumb__mark {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
}
.alice-tb-sep {
  color: var(--vp-grijs-160);
  font-weight: 400;
}

@media (max-width: 1279px) {
  .alice-shell {
    grid-template-columns: 56px 240px 1fr;
  }
  .alice-shell--compact {
    grid-template-columns: 56px 200px 1fr;
  }
  .alice-shell--collapsed {
    grid-template-columns: 56px 28px 1fr;
  }
  .alice-tb-crumb--current {
    max-width: 180px;
  }
}
</style>
